import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ClipboardList, User, Clock, Smartphone } from 'lucide-react';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatDocDate } from '@/utils/locale';

/**
 * Metadata shapes written by the Phase 2 RPCs
 * (20260912100500_tech_job_rpcs.sql, 20260912100600_tech_estimate_rpcs.sql,
 * 20260912100900_close_job_rpc.sql):
 *   tech_job_<event>                  {tech_id, reason?, from_status, to_status, job_number, by_tech}
 *   job_closed                        {invoice_id, invoice_total, payments, signed, source: tech_app|crm, job_number}
 *   tech_estimate_created             {job_id, job_number, estimate_amount, by_tech}
 *   estimate_signature_token_issued   {expires, by_tech}
 *   tech_estimate_declined            {reason?, by_tech}
 * The CRM's own trigger rows (job_created / job_updated) carry {changes: [...]}.
 */
interface AuditMetadata {
    changes?: Array<{ field: string; old_value?: unknown; new_value?: unknown }>;
    job_number?: string;
    customer_name?: string;
    reason?: string;
    from_status?: string;
    to_status?: string;
    by_tech?: boolean;
    tech_id?: string;
    invoice_id?: string;
    invoice_total?: number | string;
    payments?: number;
    signed?: boolean;
    source?: 'tech_app' | 'crm' | string;
    estimate_amount?: number | string;
    expires?: string;
    previous_decline_reason?: string;
    // review-request-send (review_requested)
    channel?: string;
    to_phone?: string | null;
    to_email?: string | null;
    invoice_number?: string;
    partial_error?: string;
    // review-page (review_rated)
    rating?: number;
    comment?: string | null;
    redirected_to?: string | null;
}

interface AuditEntry {
    id: string;
    action: string;
    entity: string;
    entity_id: string | null;
    actor_user_id: string | null;
    metadata: AuditMetadata | null;
    created_at: string;
    actor_name?: string | null;
    /** Quote number when the row belongs to an estimate on this job's lead. */
    estimate_number?: string | null;
}

// Maps raw DB field names to human-readable labels
const FIELD_LABELS: Record<string, string> = {
    status: 'Job Status',
    invoice_total: 'Job Revenue',
    quoted_price: 'Quoted Price',
    job_date: 'Job Date',
    scheduled_date: 'Scheduled Date',
    assigned_tech_id: 'Technician',
    assigned_csr_id: 'CSR',
    payment_status: 'Payment Status',
    amount_paid: 'Amount Paid',
    cancellation_reason: 'Cancellation Reason',
    issue_description: 'Issue Description',
    notes: 'Notes',
    service_type_id: 'Service Type',
    total_parts_cost: 'Parts Cost',
    total_parts_sell: 'Parts Charged',
    customer_name: 'Customer Name',
};

interface ValueFormatters {
    formatMoney: (amount: number) => string;
    formatDate: (value: string) => string;
}

function humanStatus(value: unknown): string {
    return String(value ?? '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Maps tech/csr UUID → name (loaded once per log render via inline resolution)
function formatValue(field: string, value: unknown, fmt: ValueFormatters): string {
    if (value === null || value === undefined || value === '') return '—';
    if (field === 'invoice_total' || field === 'quoted_price' || field === 'amount_paid' || field === 'total_parts_cost' || field === 'total_parts_sell') {
        const num = parseFloat(String(value));
        return isNaN(num) ? String(value) : fmt.formatMoney(num);
    }
    if (field === 'status' || field === 'payment_status') {
        return humanStatus(value);
    }
    if (field === 'job_date' || field === 'scheduled_date') {
        try {
            return fmt.formatDate(String(value)) || String(value);
        } catch { return String(value); }
    }
    // UUIDs — just show a truncated ID, real names resolved separately
    if (field === 'assigned_tech_id' || field === 'assigned_csr_id' || field === 'service_type_id') {
        return `ID: ${String(value).slice(0, 8)}…`;
    }
    const str = String(value);
    return str.length > 120 ? str.slice(0, 120) + '…' : str;
}

const ACTION_STYLES: Record<string, { label: string; color: string }> = {
    job_created: { label: 'Created', color: 'bg-success/10 text-success' },
    job_updated: { label: 'Updated', color: 'bg-info/10 text-info' },
    // Technician app (tech_set_job_progress)
    tech_job_accept: { label: 'Accepted job', color: 'bg-success/10 text-success' },
    tech_job_decline: { label: 'Declined job', color: 'bg-destructive/10 text-destructive' },
    tech_job_en_route: { label: 'En route', color: 'bg-info/10 text-info' },
    tech_job_start: { label: 'Started on site', color: 'bg-info/10 text-info' },
    tech_job_hold: { label: 'Put on hold', color: 'bg-warning/10 text-warning' },
    tech_job_resume: { label: 'Resumed', color: 'bg-info/10 text-info' },
    tech_job_leave_quote_sent: { label: 'Left – quote sent', color: 'bg-primary/10 text-primary' },
    // office_assign_tech
    tech_job_resent: { label: 'Sent to technician again', color: 'bg-warning/10 text-warning' },
    tech_job_reassigned: { label: 'Reassigned', color: 'bg-info/10 text-info' },
    tech_job_unassigned: { label: 'Unassigned', color: 'bg-muted text-muted-foreground' },
    // office_schedule_job (Schedule board)
    job_rescheduled: { label: 'Moved on the schedule', color: 'bg-warning/10 text-warning' },
    // close_job (app or CRM)
    job_closed: { label: 'Closed & invoiced', color: 'bg-success/10 text-success' },
    // Quotes on this job's lead (tech_create_estimate & co.)
    tech_estimate_created: { label: 'Quote created', color: 'bg-primary/10 text-primary' },
    estimate_signature_token_issued: { label: 'Quote sent for signature', color: 'bg-primary/10 text-primary' },
    tech_estimate_declined: { label: 'Quote declined on site', color: 'bg-destructive/10 text-destructive' },
    // review-request-send (automated, after the invoice is paid in full)
    review_requested: { label: 'Review request sent', color: 'bg-primary/10 text-primary' },
    review_rated: { label: 'Customer rated us', color: 'bg-success/10 text-success' },
};

interface JobActivityLogProps {
    jobId: string;
    /** Estimates on this lead show up on the job timeline as well. */
    leadId?: string | null;
}

export function JobActivityLog({ jobId, leadId = null }: JobActivityLogProps) {
    const { locale, timezone, formatMoney } = useCompanyLocale();
    const fmt: ValueFormatters = {
        formatMoney,
        formatDate: (value) => formatDocDate(value, `EEE, ${locale.shortDate}`, timezone),
    };
    const { data: entries = [], isLoading } = useQuery({
        // leadId is derived from the job, so the key stays ['job-audit-log', jobId] for realtime invalidation.
        queryKey: ['job-audit-log', jobId],
        queryFn: async (): Promise<AuditEntry[]> => {
            const jobRows = supabase
                .from('audit_logs')
                .select('id, action, entity, entity_id, actor_user_id, metadata, created_at')
                .eq('entity', 'job')
                .eq('entity_id', jobId)
                .order('created_at', { ascending: false });

            // Quotes raised from the app (or the office) live on the lead, not the job.
            const estimateRows = (async () => {
                if (!leadId) return { rows: [], numbers: new Map<string, string>() };
                const { data: estimates, error: estErr } = await supabase
                    .from('estimates')
                    .select('id, estimate_number')
                    .eq('lead_id', leadId);
                if (estErr) throw estErr;
                const numbers = new Map((estimates ?? []).map(e => [e.id, e.estimate_number]));
                if (numbers.size === 0) return { rows: [], numbers };
                const { data, error } = await supabase
                    .from('audit_logs')
                    .select('id, action, entity, entity_id, actor_user_id, metadata, created_at')
                    .eq('entity', 'estimate')
                    .in('entity_id', [...numbers.keys()])
                    .order('created_at', { ascending: false });
                if (error) throw error;
                return { rows: data ?? [], numbers };
            })();

            const [{ data: jobData, error: jobErr }, est] = await Promise.all([jobRows, estimateRows]);
            if (jobErr) throw jobErr;

            const merged = [...(jobData ?? []), ...est.rows]
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            if (merged.length === 0) return [];

            // Batch-fetch actor names
            const actorIds = [...new Set(merged.map(r => r.actor_user_id).filter(Boolean))] as string[];
            const nameMap = new Map<string, string>();
            if (actorIds.length > 0) {
                const { data: profiles } = await supabase
                    .from('profiles')
                    .select('id, full_name')
                    .in('id', actorIds);
                (profiles || []).forEach(p => { if (p.full_name) nameMap.set(p.id, p.full_name); });
            }

            return merged.map(row => ({
                ...row,
                metadata: row.metadata as AuditMetadata | null,
                actor_name: row.actor_user_id ? (nameMap.get(row.actor_user_id) ?? 'Unknown') : 'System',
                estimate_number: row.entity === 'estimate' && row.entity_id ? est.numbers.get(row.entity_id) ?? null : null,
            }));
        },
        staleTime: 30_000,
    });

    /** Rows written by the tech RPCs: one line of context under the badge. */
    const renderTechDetail = (entry: AuditEntry) => {
        const m = entry.metadata ?? {};
        const bits: React.ReactNode[] = [];

        if (entry.entity === 'estimate') {
            const link = entry.entity_id ? (
                <Link to={`/estimates/${entry.entity_id}`} className="font-medium text-primary hover:underline">
                    {entry.estimate_number ?? 'Quote'}
                </Link>
            ) : <span className="font-medium text-foreground">Quote</span>;
            bits.push(<span key="q">{link}</span>);
            if (m.estimate_amount !== undefined) {
                const n = parseFloat(String(m.estimate_amount));
                if (!isNaN(n)) bits.push(<span key="amt">{formatMoney(n)}</span>);
            }
            if (m.expires) bits.push(<span key="exp">valid until {fmt.formatDate(m.expires)}</span>);
        }

        if (m.from_status && m.to_status && m.from_status !== m.to_status) {
            bits.push(
                <span key="st" className="flex items-center gap-1">
                    <span className="line-through opacity-60">{humanStatus(m.from_status)}</span>
                    <span className="opacity-40">→</span>
                    <span className="text-foreground font-medium">{humanStatus(m.to_status)}</span>
                </span>,
            );
        }
        if (m.reason) bits.push(<span key="r">Reason: <span className="text-foreground">{m.reason}</span></span>);
        if (m.previous_decline_reason) bits.push(<span key="pr">Had declined: <span className="text-foreground">{m.previous_decline_reason}</span></span>);

        if (entry.action === 'job_closed') {
            if (m.invoice_total !== undefined) {
                const n = parseFloat(String(m.invoice_total));
                if (!isNaN(n)) bits.push(<span key="tot">Invoice <span className="text-foreground font-medium">{formatMoney(n)}</span></span>);
            }
            if (typeof m.payments === 'number' && m.payments > 0) bits.push(<span key="pay">{m.payments} payment{m.payments === 1 ? '' : 's'} recorded</span>);
            bits.push(<span key="sig">{m.signed ? 'Customer signed on the device' : 'No signature'}</span>);
            bits.push(<span key="src">{m.source === 'tech_app' ? 'Closed from the Steddy app' : 'Closed from the CRM'}</span>);
        }

        if (entry.action === 'review_rated') {
            if (typeof m.rating === 'number') bits.push(<span key="stars" className="text-foreground font-medium">{'★'.repeat(m.rating)}{'☆'.repeat(Math.max(0, 5 - m.rating))} {m.rating}/5</span>);
            if (m.comment) bits.push(<span key="c">“{m.comment}”</span>);
            if (m.redirected_to) bits.push(<span key="rd">then sent to the review site</span>);
        }

        if (entry.action === 'review_requested') {
            const to = [m.to_phone, m.to_email].filter(Boolean).join(' and ');
            if (to) bits.push(<span key="to">Sent to <span className="text-foreground">{to}</span></span>);
            if (m.invoice_number) bits.push(<span key="inv">for {m.invoice_number}</span>);
            if (m.partial_error) bits.push(<span key="pe" className="text-warning">One channel failed</span>);
        }

        if (bits.length === 0) return null;
        return (
            <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
                {bits}
            </div>
        );
    };

    return (
        <Card>
            <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                        <ClipboardList className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <CardTitle className="text-lg">Activity Log</CardTitle>
                </div>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="flex gap-3">
                                <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                                <div className="space-y-2 flex-1">
                                    <Skeleton className="h-4 w-48" />
                                    <Skeleton className="h-3 w-full" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : entries.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                        <ClipboardList className="h-8 w-8 opacity-30" />
                        <p className="text-sm">No activity recorded yet.</p>
                        <p className="text-xs opacity-70">Changes made from now on will appear here.</p>
                    </div>
                ) : (
                    <div className="relative">
                        {/* Vertical line */}
                        <div className="absolute left-3.5 top-0 bottom-0 w-px bg-border" />
                        <div className="space-y-5">
                            {entries.map(entry => {
                                const style = ACTION_STYLES[entry.action] ?? { label: humanStatus(entry.action), color: 'bg-muted text-muted-foreground' };
                                const changes = entry.metadata?.changes ?? [];
                                const fromApp = entry.metadata?.by_tech === true || entry.metadata?.source === 'tech_app';
                                const ts = formatDocDate(entry.created_at, `${locale.shortDate}, h:mm a`, timezone);

                                return (
                                    <div key={entry.id} className="relative flex gap-4 pl-8">
                                        {/* Dot */}
                                        <div className="absolute left-0 top-1 h-7 w-7 rounded-full bg-background border-2 border-border flex items-center justify-center flex-shrink-0">
                                            {fromApp
                                                ? <Smartphone className="h-3.5 w-3.5 text-info" />
                                                : <User className="h-3.5 w-3.5 text-muted-foreground" />}
                                        </div>

                                        <div className="flex-1 min-w-0 pb-1">
                                            {/* Header row */}
                                            <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                                <span className="font-semibold text-sm">{entry.actor_name}</span>
                                                <Badge variant="secondary" className={`text-xs px-1.5 py-0 ${style.color}`}>
                                                    {style.label}
                                                </Badge>
                                                {fromApp && (
                                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-info border-info/30">
                                                        Steddy app
                                                    </Badge>
                                                )}
                                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                    <Clock className="h-3 w-3" />
                                                    {ts}
                                                </span>
                                            </div>

                                            {/* Change details (trigger rows) */}
                                            {changes.length > 0 && (
                                                <div className="space-y-1">
                                                    {changes.map((change, i) => (
                                                        <div key={i} className="text-xs text-muted-foreground flex flex-wrap items-center gap-1">
                                                            <span className="font-medium text-foreground">
                                                                {FIELD_LABELS[change.field] ?? change.field}
                                                            </span>
                                                            {change.old_value !== undefined ? (
                                                                <>
                                                                    <span className="line-through opacity-60">{formatValue(change.field, change.old_value, fmt)}</span>
                                                                    <span className="opacity-40">→</span>
                                                                    <span className="text-foreground font-medium">{formatValue(change.field, change.new_value, fmt)}</span>
                                                                </>
                                                            ) : (
                                                                <span className="text-foreground font-medium">set to {formatValue(change.field, change.new_value, fmt)}</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Tech / close / quote rows */}
                                            {changes.length === 0 && renderTechDetail(entry)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
